import React, { useState } from "react";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { axiosReqConfig } from "../../functions/axiosRequest";
import { API_IP } from "../../global_variables";
import PaymentMethodPicker from "./PaymentMethodPicker";
import "./PaymentModal.css";

const PaymentModal = ({
  handleClose,
  showModal,
  toggleAlert,
  toggleRefresh,
  clientSecret,
  customerSession,
  totalPrice,
  selectedBooking,
  paymentMethod,
  onSelectMethod,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isFinished, setIsFinished] = useState(false);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setMessage("Stripe is not ready. Please wait a moment and try again.");
      return;
    }

    setIsLoading(true);
    setMessage(null);

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      if (error.type === "card_error" || error.type === "validation_error") {
        setMessage(error.message || "An unexpected error occurred. Try again");
      } else {
        setMessage("An unexpected error occurred. Try again");
      }
    } else {
      const res = await axiosReqConfig(`${API_IP}/api/payment`, "patch", {
        paymentToken: selectedBooking.transactionId,
        booking_id: selectedBooking.booking_id,
        provider_id: selectedBooking.provider_id,
        platform: "web",
      });
      if (!res?.ok) {
        setMessage("Payment was received but booking update failed. Please contact support.");
      } else {
        toggleRefresh();
        setMessage("Your payment has been processed successfully!");
      }
    }

    setIsFinished(true);
    setIsLoading(false);
  };

  function handleCloseModal() {
    setIsFinished(false);
    setMessage(null);
    setIsLoading(false);
    handleClose();
  }

  if (!showModal) {
    return null;
  }

  const isError = message && (message.toLowerCase().includes("error") || message.toLowerCase().includes("try again"));
  const iconName = isError ? "booking_denied_icon.png" : "booking_accepted_icon.webp";

  return (
    <>
      <div className="PM-overlay" onClick={handleCloseModal}>
        <div
          className="PM-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          {onSelectMethod && !isFinished && (
            <PaymentMethodPicker selected={paymentMethod || "card"} onSelect={onSelectMethod} />
          )}
          {!isFinished ? (
            <form
              id="payment-form"
              onSubmit={handleSubmit}
              className="PM-form"
            >
              <div className="PM-header">
                <h2>Secure Payment</h2>
                <p>Complete your booking securely via Stripe</p>
              </div>

              {selectedBooking && selectedBooking.care_service_name && (
                <div className="PM-summary">
                  <h3 className="PM-summary-title">Order Summary</h3>
                  <div className="PM-summary-row">
                    <span>Service</span>
                    <strong>{selectedBooking.care_service_name}</strong>
                  </div>
                  <div className="PM-summary-row">
                    <span>Provider</span>
                    <strong>{selectedBooking.provider_name}</strong>
                  </div>
                  <div className="PM-summary-row">
                    <span>Start Date</span>
                    <strong>{new Date(selectedBooking.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</strong>
                  </div>
                  <div className="PM-summary-row PM-summary-total">
                    <span>Total Amount</span>
                    <strong>${(totalPrice / 100).toFixed(2)}</strong>
                  </div>
                </div>
              )}

              <div className="PM-body">
                <PaymentElement
                  id="payment-element"
                  options={{ layout: "tabs" }}
                />
              </div>

              {message && (
                <div
                  className={`PM-message ${isError ? "PM-error" : "PM-success"}`}
                >
                  {message}
                </div>
              )}

              <div className="PM-footer">
                <button
                  disabled={isLoading || !stripe || !elements}
                  id="submit"
                  className="PM-submit-btn"
                >
                  {isLoading ? (
                    <div className="PM-loading-wrapper">
                      <img
                        src={require(`../../images/loading_icon.gif`)}
                        alt="Loading"
                        className="PM-loading-icon"
                      />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    `Pay Now $${(totalPrice / 100).toFixed(2)}`
                  )}
                </button>
                <button type="button" className="PM-cancel-btn" onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="PM-alert-content">
              <img
                src={require(`../../images/${iconName}`)}
                alt="Status"
                className="PM-status-icon"
              />
              <h3>Payment Status</h3>
              <p>{message}</p>
              <button className="PM-close-btn" onClick={handleCloseModal}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentModal;
